// All static site copy. Verbatim from the design handoff — the couple should
// confirm names, rates and codes before launch.

export const WEDDING = {
  brideFirst: "Aimee",
  groomFirst: "Benjamin",
  dateLabel: "July 9, 2027",
  venueLine: "Stone Tower Winery · Leesburg, Virginia",
  venueShort: "Stone Tower Winery",
  /** Ceremony start, used by the hero countdown. */
  ceremonyAt: "2027-07-09T16:30:00",
  rsvpDeadlineLabel: "May 1, 2027",
  plannerEmail: "hello@aimeeandben.com",
} as const;

/** Sections still being finished: shown on the local dev server, hidden on the
 *  deployed site. `import.meta.env.PROD` is true only in `npm run build`. */
export const HIDDEN_SECTIONS: readonly string[] = import.meta.env.PROD
  ? ["story", "schedule", "party", "gallery", "registry", "rsvp"]
  : [];
export const isSectionShown = (id: string) => !HIDDEN_SECTIONS.includes(id);

export const NAV_LINKS = [
  { href: "#story", label: "Our Story" },
  { href: "#schedule", label: "Schedule" },
  { href: "#party", label: "Wedding Party" },
  { href: "#gallery", label: "Gallery" },
  { href: "#travel", label: "Travel & Stay" },
  { href: "#registry", label: "Registry" },
  { href: "#faq", label: "FAQ" },
].filter((link) => isSectionShown(link.href.slice(1)));

export const STORY_PARAGRAPHS = [
  "We met in the autumn of 2019, in line for coffee at a farmers market in Del Ray. Ben was buying apples he did not need. Aimee offered him a better recommendation, and he took it.",
  "Six years, two apartments, one very opinionated rescue dog and a lot of long drives later, we found ourselves back in wine country on a quiet Sunday in October.",
];

export const STORY_QUOTE =
  "He asked on the terrace at Stone Tower. She said yes before he finished.";

export const STORY_CLOSER =
  "We can't wait to celebrate with you at the place where it started.";

export const STORY_META = [
  { label: "Met", value: "October 2019" },
  { label: "Engaged", value: "October 2025" },
  { label: "Married", value: "July 2027" },
];

export interface ScheduleEvent {
  day: string;
  name: string;
  when: string;
  where: string;
  detail: string;
  /** Google Maps link for "Get directions". */
  directions: string;
}

export const SCHEDULE: ScheduleEvent[] = [
  {
    day: "Thursday, July 8",
    name: "Welcome Drinks",
    when: "6:00 pm – 9:00 pm",
    where: "Lost Rhino Brewing, Ashburn",
    detail: "Come as you are. Beer, a taco truck, and everyone who got in early.",
    directions: "https://maps.google.com/?q=Lost+Rhino+Brewing+Company+Ashburn+VA",
  },
  {
    day: "Friday, July 9",
    name: "Ceremony",
    when: "4:30 pm",
    where: "Tower Lawn, Stone Tower Winery",
    detail: "Doors at 4:00 pm. The lawn is grass and the sun is bright — hats welcome.",
    directions: "https://maps.google.com/?q=Stone+Tower+Winery+Leesburg+VA",
  },
  {
    day: "Friday, July 9",
    name: "Reception",
    when: "6:00 pm – 11:00 pm",
    where: "Wild Boar Barn, Stone Tower Winery",
    detail: "Dinner, toasts, and a band that takes requests. Last shuttle leaves at 11:30.",
    directions: "https://maps.google.com/?q=Stone+Tower+Winery+Leesburg+VA",
  },
  {
    day: "Saturday, July 10",
    name: "Farewell Brunch",
    when: "10:00 am – 12:00 pm",
    where: "Embassy Suites by Hilton Dulles North Loudoun",
    detail: "Drop in before you drive home. No need to RSVP separately.",
    directions: "https://maps.google.com/?q=Embassy+Suites+by+Hilton+Dulles+North+Loudoun",
  },
];

export const DRESS_CODE_NOTE =
  "Dress code: garden formal. The ceremony lawn is grass — block heels are your friend.";

export const PARTY = [
  {
    name: "Priya Raghavan",
    role: "Maid of Honor",
    note: "Aimee's older sister, and the one who taught her to sail. She has been planning this weekend since roughly 2011.",
  },
  {
    name: "Dev Anand",
    role: "Best Man",
    note: "Ben's roommate since freshman year. The speech has been written, rewritten, and lawyered. Nobody is safe.",
  },
  {
    name: "Nora Whitfield",
    role: "Bridesmaid",
    note: "Met Aimee in line at the Del Ray farmers market and stayed for a decade. Technically she was there first.",
  },
  {
    name: "Camille Duarte",
    role: "Bridesmaid",
    note: "Cousin, colorist, keeper of every group chat. If you need to know anything about the weekend, ask her.",
  },
  {
    name: "Theo Marchetti",
    role: "Groomsman",
    note: "Taught Ben to cook. Results have been mixed, but the man keeps showing up with groceries.",
  },
  {
    name: "Isaac Bell",
    role: "Groomsman",
    note: "Ben's younger brother. He will be the first one on the dance floor and the last one off it.",
  },
  {
    name: "Hana Sato",
    role: "Bridesmaid",
    note: "Grad school lab partner turned lifelong friend. She has read every draft of the vows and said nothing.",
  },
  {
    name: "Wally",
    role: "Ring Bearer",
    note: "A nine-year-old beagle with exactly one job. We are cautiously optimistic.",
  },
];

export const GALLERY = [
  { label: "ENGAGEMENT 01", height: 380, speed: 0.16 },
  { label: "ENGAGEMENT 02", height: 300, speed: 0.3 },
  { label: "ENGAGEMENT 03", height: 440, speed: 0.08 },
  { label: "THE PROPOSAL", height: 320, speed: 0.26 },
  { label: "VINEYARD ROWS", height: 400, speed: 0.12 },
  { label: "WALLY, BEST DOG", height: 340, speed: 0.22 },
];

export const GALLERY_NOTE =
  "Engagement photos by a photographer we have not booked yet. Drop the real files into these frames.";

/** Pins for the Travel map. Coordinates are [lat, lng]. */
export const MAP_PINS = [
  {
    name: "Stone Tower Winery",
    label: "The Winery",
    note: "Ceremony & reception",
    position: [39.0534, -77.6414] as [number, number],
    directions: "https://maps.google.com/?q=Stone+Tower+Winery+Leesburg+VA",
  },
  {
    name: "Embassy Suites by Hilton Dulles North Loudoun",
    label: "The Hotel",
    note: "Our hotel block",
    position: [39.0141, -77.462] as [number, number],
    directions: "https://maps.google.com/?q=Embassy+Suites+by+Hilton+Dulles+North+Loudoun",
  },
];

export const MAP_TITLE = "Map showing Stone Tower Winery and the Embassy Suites hotel";

export const HOTELS = [
  {
    name: "Embassy Suites by Hilton Dulles North Loudoun",
    distance: "Our wedding block",
    detail: "Rooms are held for our guests from July 7 to July 10, 2027. The link fills in the dates and group code for you. Hilton Honors members can use the code too, so you can book while signed in and earn points.",
    rate: "Group rate, July 7 – 10, 2027",
    code: "CES915",
    url: "https://www.hilton.com/en/book/reservation/deeplink/?ctyhocn=WASNLES&groupCode=CES915&arrivaldate=2027-07-07&departuredate=2027-07-10&cid=OM,WW,HILTONLINK,EN,DirectLink&fromId=HILTONLINKDIRECT",
  },
];

export const TRAVEL_INFO = [
  {
    title: "Airports",
    body: "Dulles (IAD) is the easy choice, about 10 minutes and 6 miles from the Embassy Suites. Reagan National (DCA) is about 35 miles, 45 to 60 minutes depending on traffic. Baltimore (BWI) is about 65 miles, 75 to 90 minutes. The winery is a further 25 minutes from the hotel.",
  },
];

export const REGISTRY_BLURB =
  "Your presence is the whole point. If you would like to give something anyway, we have registered in a few places and set aside a fund for the honeymoon in Portugal.";

// TODO: swap these for the couple's real registry URLs before launch.
export const REGISTRIES = [
  { name: "Crate & Barrel", detail: "Kitchen and table", url: "https://www.crateandbarrel.com/gift-registry/" },
  { name: "Zola", detail: "A bit of everything", url: "https://www.zola.com/registry/" },
  { name: "Honeymoon Fund", detail: "Two weeks in Portugal", url: "https://www.zola.com/registry/" },
  { name: "Loudoun Food Bank", detail: "Give in our name", url: "https://loudounhunger.org/" },
];

export const FAQS = [
  {
    q: "Can I bring a guest?",
    a: "Your invitation names everyone we have saved a seat for. If it says “and guest,” please add their name on the RSVP form so the caterer knows.",
  },
  {
    q: "Are children welcome?",
    a: "We love your kids, and we have planned an adults-only evening.",
  },
  {
    q: "What is the dress code?",
    a: "Black tie optional. Tuxedos or dark suits, and floor-length or elegant cocktail dresses. The ceremony is on a lawn, so choose heels that will not sink.",
  },
  {
    q: "Is the ceremony outside?",
    a: "Yes, on the Tower Lawn overlooking the vineyard. If the weather turns, everything moves indoors.",
  },
];

/** Entrée options. Must match what the caterer is expecting. */
export const MEALS = [
  "Beef short rib",
  "Chesapeake rockfish",
  "Summer squash risotto",
];

/** Side events a household can opt into, with their default state. */
export const RSVP_EVENTS: Record<string, boolean> = {
  "Welcome drinks": true,
  "Ceremony & reception": true,
  "Farewell brunch": false,
};
